import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Switch } from '@/components/ui/switch';
import { CheckCircle, Star, Briefcase, Rocket, Building2 } from 'lucide-react';
import { motion } from 'framer-motion';
import { Separator } from '@/components/ui/separator';
import { useNavigate } from 'react-router-dom';

// Define the type for plan actions
type PlanAction = 'auth' | 'contact';

const plans: Array<{
  name: string;
  icon: JSX.Element;
  price: { monthly: number; yearly: number };
  description: string;
  features: string[];
  cta: string;
  highlight: boolean;
  badge: string;
  action: PlanAction;
}> = [
  {
    name: 'Free',
    icon: <Star className="h-7 w-7 text-primary" />, 
    price: { monthly: 0, yearly: 0 },
    description: 'Basic features for individuals and hobbyists.',
    features: [
      '1 Tournament',
      'Up to 2 Teams',
      'Basic Scoring',
      'Community Support',
    ],
    cta: 'Get Started',
    highlight: false,
    badge: 'Free',
    action: 'auth',
  },
  {
    name: 'Plus',
    icon: <Rocket className="h-7 w-7 text-primary" />, 
    price: { monthly: 9, yearly: 90 },
    description: 'For small clubs and regular organizers.',
    features: [
      'Up to 5 Tournaments',
      'Up to 10 Teams',
      'Live Scoreboard',
      'Email Support',
    ],
    cta: 'Upgrade',
    highlight: false,
    badge: 'Popular',
    action: 'auth',
  },
  {
    name: 'Pro',
    icon: <Briefcase className="h-7 w-7 text-primary" />, 
    price: { monthly: 19, yearly: 190 },
    description: 'Advanced features for leagues and organizations.',
    features: [
      'Unlimited Tournaments',
      'Unlimited Teams',
      'Advanced Analytics',
      'Priority Support',
      'Custom Branding',
    ],
    cta: 'Go Pro',
    highlight: true,
    badge: 'Best Value',
    action: 'auth',
  },
  {
    name: 'Enterprise',
    icon: <Building2 className="h-7 w-7 text-primary" />, 
    price: { monthly: 49, yearly: 490 },
    description: 'Custom solutions for federations and enterprises.',
    features: [
      'All Pro Features',
      'Dedicated Manager',
      'Custom Integrations',
      'SLA & Onboarding',
    ],
    cta: 'Contact Sales',
    highlight: false,
    badge: 'Enterprise',
    action: 'contact',
  },
];

const featureList = [
  'Tournaments',
  'Teams',
  'Live Scoreboard',
  'Advanced Analytics',
  'Custom Branding',
  'Priority Support',
  'Dedicated Manager',
  'Custom Integrations',
];

const planFeatures = [
  ['1', '2', false, false, false, false, false, false], // Free
  ['5', '10', true, false, false, true, false, false], // Plus
  ['Unlimited', 'Unlimited', true, true, true, true, false, false], // Pro
  ['Unlimited', 'Unlimited', true, true, true, true, true, true], // Enterprise
];

// Discount percent for yearly billing
const DISCOUNT_PERCENT = 15;

// Helper to calculate discounted price
function getDiscountedPrice(original: number, percent: number) {
  return Math.round(original * (1 - percent / 100));
}

// Add a helper to calculate discounted yearly price
function getPlanPrice(plan: typeof plans[number], billing: 'monthly' | 'yearly') {
  if (billing === 'monthly') {
    return { price: plan.price.monthly, original: undefined };
  }
  const original = plan.price.monthly * 12;
  const discounted = getDiscountedPrice(original, DISCOUNT_PERCENT);
  return { price: discounted, original };
}

const PricingPage = () => {
  const [billing, setBilling] = useState<'monthly' | 'yearly'>('monthly');
  const navigate = useNavigate();

  // Handler for plan card buttons
  const handlePlanAction = (action: 'auth' | 'contact') => {
    if (action === 'auth') {
      navigate('/auth');
    } else if (action === 'contact') {
      navigate('/contact');
    }
  };

  return (
    <div className="container mx-auto px-4 py-12 max-w-7xl">
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: 'easeOut' }}
        className="text-center mb-12"
      >
        <h1 className="text-4xl md:text-5xl font-bold mb-4 text-foreground">Pricing Plans</h1>
        <p className="text-lg md:text-xl text-muted-foreground mb-6">Choose the plan that fits your cricket journey. Simple, transparent pricing for everyone.</p>
        <div className="flex items-center justify-center gap-4">
          <span className={billing === 'monthly' ? 'font-semibold text-primary' : 'text-muted-foreground'}>Monthly</span>
          <Switch checked={billing === 'yearly'} onCheckedChange={v => setBilling(v ? 'yearly' : 'monthly')} />
          <span className={billing === 'yearly' ? 'font-semibold text-primary' : 'text-muted-foreground'}>
            Yearly <Badge
              className={
                billing === 'yearly'
                  ? 'ml-1 transition-colors duration-200'
                  : 'ml-1 transition-colors duration-200 bg-gray-400 text-white border-gray-400'
              }
              variant={billing === 'yearly' ? 'secondary' : undefined}
            >
              Save {DISCOUNT_PERCENT}%
            </Badge>
          </span>
        </div>
      </motion.div>

      {/* Pricing Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
        {plans.map((plan, idx) => {
          const { price, original } = getPlanPrice(plan, billing);
          return (
            <motion.div
              key={plan.name}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 + idx * 0.1, ease: 'easeOut' }}
              viewport={{ once: true }}
            >
              <Card className={`relative group shadow-lg hover:shadow-2xl transition-shadow duration-300 border-2 ${plan.highlight ? 'border-primary scale-105 z-10' : 'border-transparent'} ${plan.highlight ? 'bg-gradient-to-br from-primary/10 to-secondary/10' : ''}`}>
                {plan.highlight && (
                  <Badge className="absolute top-4 right-4 z-20" variant="default">{plan.badge}</Badge>
                )}
                {!plan.highlight && (
                  <Badge className="absolute top-4 right-4 z-20" variant="secondary">{plan.badge}</Badge>
                )}
                <CardHeader className="flex flex-col items-center gap-2 pb-2">
                  {plan.icon}
                  <CardTitle className="text-2xl font-bold text-center">{plan.name}</CardTitle>
                  <CardDescription className="text-center text-base min-h-[48px]">{plan.description}</CardDescription>
                </CardHeader>
                <CardContent className="flex flex-col items-center">
                  <div className="flex items-end gap-1 mb-2">
                    {price !== 0 && billing === 'yearly' && original && original > 0 && (
                      <span className="text-base text-muted-foreground mb-1 line-through mr-2">${original}</span>
                    )}
                    <span className="text-4xl font-extrabold text-foreground">{plan.name === 'Free' ? 'Free' : `$${price}`}</span>
                    {plan.name !== 'Free' && price !== 0 && (
                      <span className="text-base text-muted-foreground mb-1">/ {billing === 'monthly' ? 'mo' : 'yr'}</span>
                    )}
                  </div>
                  <ul className="space-y-2 my-4 w-full">
                    {plan.features.map((feature, i) => (
                      <li key={i} className="flex items-center gap-2 text-sm text-foreground/90">
                        <CheckCircle className="h-4 w-4 text-primary" /> {feature}
                      </li>
                    ))}
                  </ul>
                </CardContent>
                <CardFooter className="flex flex-col items-center">
                  <Button className={`w-full ${plan.highlight ? 'bg-primary text-primary-foreground hover:bg-primary/90' : ''}`}
                    onClick={() => handlePlanAction(plan.action as PlanAction)}
                  >
                    {plan.cta}
                  </Button>
                </CardFooter>
              </Card>
            </motion.div>
          );
        })}
      </div>

      {/* Feature Comparison Table */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: 'easeOut' }}
        viewport={{ once: true }}
        className="bg-card rounded-xl shadow-lg p-6 md:p-10 mb-16"
      >
        <h2 className="text-2xl font-bold mb-6 text-center">Compare Features</h2>
        <div className="overflow-x-auto">
          <table className="min-w-full border-separate border-spacing-y-2">
            <thead>
              <tr>
                <th className="text-left text-muted-foreground font-semibold text-sm py-2">Feature</th>
                {plans.map((plan, idx) => (
                  <th key={plan.name} className={`text-center font-semibold text-sm py-2 ${plan.highlight ? 'text-primary' : 'text-foreground'}`}>{plan.name}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {featureList.map((feature, i) => (
                <tr key={feature} className="border-b last:border-b-0">
                  <td className="py-2 text-sm text-foreground/90 font-medium">{feature}</td>
                  {planFeatures.map((plan, j) => (
                    <td key={j} className="text-center">
                      {typeof plan[i] === 'string' ? (
                        <span className="font-semibold">{plan[i]}</span>
                      ) : plan[i] === true ? (
                        <CheckCircle className="inline h-5 w-5 text-primary" />
                      ) : (
                        <span className="text-muted-foreground">—</span>
                      )}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </motion.div>

      {/* Call to Action */}
      <motion.div
        initial={{ opacity: 0, y: 40 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.7, ease: 'easeOut' }}
        viewport={{ once: true }}
        className="text-center"
      >
        <Card className="inline-block bg-gradient-to-r from-primary/10 to-secondary/10 border-primary/20 shadow-xl">
          <CardContent className="pt-8 pb-8 px-8">
            <h2 className="text-2xl font-bold mb-2 text-foreground">Ready to elevate your cricket experience?</h2>
            <p className="text-muted-foreground mb-6">Start for free or unlock advanced features with a paid plan. No credit card required for Free plan.</p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button className="bg-primary text-primary-foreground px-6 py-3 rounded-lg font-semibold hover:bg-primary/90 transition-colors"
                onClick={() => handlePlanAction('auth' as const)}
              >
                Start Free
              </Button>
              <Button variant="outline" className="px-6 py-3 rounded-lg font-semibold hover:bg-accent transition-colors"
                onClick={() => handlePlanAction('contact' as const)}
              >
                Contact Sales
              </Button>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </div>
  );
};

export default PricingPage; 